import { useEffect, useCallback, type ChangeEvent } from 'react';
import { useTranslation } from '../i18n/useTranslation';
import { Select } from './atoms/Select/Select';

const SUPPORTED_LANGUAGES = ['en', 'de', 'fr', 'ja', 'es', 'it', 'nl'] as const;
type SupportedLanguage = typeof SUPPORTED_LANGUAGES[number];

function isSupportedLanguage(lang: string): lang is SupportedLanguage {
  return (SUPPORTED_LANGUAGES as readonly string[]).includes(lang);
}

export interface LanguageSwitcherProps {
  /**
   * Compact header use: no visible "Language" label above the selector. The selected language
   * name names the control visually; `aria-label` keeps it named for assistive technology.
   */
  hideLabel?: boolean;
}

export function LanguageSwitcher({ hideLabel = false }: LanguageSwitcherProps = {}) {
  const { t, i18n } = useTranslation();

  const activeLanguage: SupportedLanguage = isSupportedLanguage(i18n.resolvedLanguage ?? '')
    ? (i18n.resolvedLanguage as SupportedLanguage)
    : 'en';

  useEffect(() => {
    document.documentElement.lang = activeLanguage;
  }, [activeLanguage]);

  const handleChange = useCallback(
    (event: ChangeEvent<HTMLSelectElement>) => {
      const value = event.target.value;
      if (!isSupportedLanguage(value)) return;
      void i18n.changeLanguage(value);
      document.documentElement.lang = value;
    },
    [i18n],
  );

  return (
    <Select
      label={hideLabel ? undefined : t('settings.languageLabel')}
      value={activeLanguage}
      onChange={handleChange}
      size="sm"
      aria-label={t('settings.languageLabel')}
    >
      <option value="en">{t('settings.languageOptions.en')}</option>
      <option value="de">{t('settings.languageOptions.de')}</option>
      <option value="fr">{t('settings.languageOptions.fr')}</option>
      <option value="ja">{t('settings.languageOptions.ja')}</option>
      <option value="es">{t('settings.languageOptions.es')}</option>
      <option value="it">{t('settings.languageOptions.it')}</option>
      <option value="nl">{t('settings.languageOptions.nl')}</option>
    </Select>
  );
}

export default LanguageSwitcher;
